import {cargoApiURL} from '@yandex-delivery/client-core/api/corp'
import {fetch as fetchBase} from '@yandex-delivery/core/utils/network'
import type {Optional} from '@yandex-delivery/types'
import type {
  OptimizeRouteResultRequest,
  OptimizeRouteResultResponse,
  OptimizeRouteStartRequest,
} from '@yandex-delivery/types/schemas/services/cargo-claims/definitions/optimize-route'
import {addQuery, delay, flow, get, toNumber, pipe} from '@yandex-delivery/utils'

import type {MakeUrlApi, Point, TTaskInfo} from './types'
import {withCsrfMiddleware} from '../../../../api/fetch'

const DELAY_TIMEOUT_MS = 1000

const fetch = pipe(fetchBase, withCsrfMiddleware)

export function fetchOptimizedRoute(
  clientId: string,
  body: OptimizeRouteStartRequest,
  country?: string,
  makeUrl: MakeUrlApi = cargoApiURL,
): Promise<OptimizeRouteResultResponse> {
  return fetch<OptimizeRouteStartRequest, TTaskInfo, TTaskInfo>({
    method: 'POST',
    headers: {
      'X-B2B-Client-ID': clientId,
    },
    url: addQuery(makeUrl('cargo-claims/v2/optimize-route/start'), {country}),
    body,
  }).then(result => pollOptimizeRoutResult(clientId, result, country, makeUrl))
}

function fetchOptimizeRouteResult(
  clientId: string,
  body: OptimizeRouteResultRequest,
  country?: string,
  makeUrl: MakeUrlApi = cargoApiURL,
): Promise<OptimizeRouteResultResponse> {
  return fetch({
    method: 'POST',
    url: addQuery(makeUrl('cargo-claims/v2/optimize-route/result'), {country}),
    headers: {
      'X-B2B-Client-ID': clientId,
    },
    body,
  })
}

function pollOptimizeRoutResult(
  clientId: string,
  taskInfo: TTaskInfo,
  country?: string,
  makeUrl: MakeUrlApi = cargoApiURL,
): Promise<OptimizeRouteResultResponse> {
  return delay(DELAY_TIMEOUT_MS)
    .then(() => fetchOptimizeRouteResult(clientId, taskInfo, country, makeUrl))
    .then(result => {
      if (result.info.status === 'completed') {
        return result
      }

      return pollOptimizeRoutResult(clientId, result.info, country, makeUrl)
    })
}

export const getPointId: (point?: Point) => Optional<string> = get('_id')

const getRoutePointId = flow(getPointId, String)
const getRoutePointLon = flow(get('address.coordinates[0]'), toNumber)
const getRoutePointLat = flow(get('address.coordinates[1]'), toNumber)

export const toSourceRouteChangePoint = (point: Point) => ({
  type: 'source',
  lon: getRoutePointLon(point),
  lat: getRoutePointLat(point),
  id: getRoutePointId(point),
})

export const toDestinationRouteChangePoint = (point: Point) => ({
  type: 'destination',
  lon: getRoutePointLon(point),
  lat: getRoutePointLat(point),
  id: getRoutePointId(point),
})
